<script setup>
  import { log } from '@/services/log'
  import { ref, inject, computed, watch } from 'vue'
  import { useRemoteCommandConfigsStore } from '@/stores/common/remote-command-configs'
  import { NButton, NForm, NGrid, NFormItemGi, NInput, NFlex, NSelect, NModal } from 'naive-ui'
  import ArsSmartSelect from '@/components/dropdowns/ars-smart-select.vue'
  import { v4 as uuidv4 } from 'uuid'

  const store = useRemoteCommandConfigsStore()
  const dx = inject('dexie')

  const formElementRef = ref(null)
  const filterTypeOptions = [
    {
      label: 'Site',
      value: 'site',
    },
    {
      label: 'Reader',
      value: 'reader',
    },
    {
      label: 'Readzone',
      value: 'readzone',
    },
    {
      label: 'Config',
      value: 'config',
    },
  ]
  const formData = ref({
    allParams: [],
    filterType: null,
    selectedSites: null,
    selectedReaders: null,
    selectedReadzones: null,
    selectedConfigs: null,
    username: null,
    password: null,
  })

  const hasReader = computed(() => store.remoteCommandConfigRowData.reader != null)
  const commandLabel = computed(() => store.remoteCommandConfigRowData.label)
  const isSelectedBySite = computed(() => formData.value.filterType === 'site')
  const isSelectedByReadzone = computed(() => formData.value.filterType === 'readzone')
  const isSelectedByReader = computed(() => formData.value.filterType === 'reader')
  const isSelectedByConfig = computed(() => formData.value.filterType === 'config')
  const prepareCommands = async () => {
    let assignedReaders = []
    if (formData.value.filterType === 'site') {
      const readers = await dx.table('Readers').toArray()
      assignedReaders = readers.filter((d) => formData.value.selectedSites.includes(d?.site))
    } else if (formData.value.filterType === 'readzone') {
      const readers = await dx.table('Readers').toArray()
      assignedReaders = readers.filter((d) => formData.value.selectedReadzones.includes(d?.readzone))
    } else if (formData.value.filterType === 'reader') {
      const readers = await dx.table('Readers').toArray()
      assignedReaders = readers.filter((d) => formData.value.selectedReaders.includes(d?.externalId))
    } else if (formData.value.filterType === 'config') {
      const readers = await dx.table('Readers').toArray()
      assignedReaders = readers.filter((d) => formData.value.selectedConfigs.includes(d?.config))
    } else {
      assignedReaders = [store.remoteCommandConfigRowData.reader]
    }

    const cache = {}
    formData.value.allParams.forEach((d) => (cache[d.name] = d.value))

    const remoteCommands = assignedReaders.map((d) => ({
      mac: d?.macAddress.replace(/:/g, '').toLowerCase(),
      commandUuid: store.remoteCommandConfigRowData.externalId,
      uuid: uuidv4().split('-')?.join('')?.toLowerCase(),
      type: 'RemoteCommand',
      name: d?.name,
      username: formData.value.username,
      password: formData.value.password,
      mode: store.remoteCommandConfigRowData.mode,
      params: cache,
    }))
    log('In remote command form', remoteCommands)
  }

  watch(
    () => store.showRemoteCommandConfigModal,
    (isVisible) => {
      if (isVisible) {
        formData.value['username'] = store.remoteCommandConfigRowData.username
      }
    }
  )
</script>
<template>
  <NModal
    v-model:show="store.showRemoteCommandConfigModal"
    preset="dialog"
    title="Remote Command Options"
    :closable="true"
    style="width: 70vw"
  >
    <NFlex vertical>
      <NForm
        ref="formElementRef"
        :model="formData"
      >
        <NGrid
          :span="24"
          :x-gap="24"
        >
          <NFormItemGi
            path="label"
            label="Command"
            span="24"
          >
            <NInput
              readonly
              v-model:value="commandLabel"
              placeholder="Enter command"
            />
          </NFormItemGi>
          <NFormItemGi
            path="filterType"
            label="Filter Type"
            span="12"
            v-if="!hasReader"
          >
            <NSelect
              v-model:value="formData.filterType"
              placeholder="Pick a Filter"
              :options="filterTypeOptions"
            />
          </NFormItemGi>
          <NFormItemGi
            path="site"
            label="Site"
            span="12"
            v-if="isSelectedBySite"
          >
            <ArsSmartSelect
              v-model:value="formData.selectedSites"
              :multiple="true"
              :clearable="true"
              placeholder="Select a site"
              :filterable="true"
              service-uri="/sites"
              options-label="name"
            />
          </NFormItemGi>
          <NFormItemGi
            path="selectedConfigs"
            label="Config"
            span="12"
            v-else-if="isSelectedByConfig"
          >
            <ArsSmartSelect
              v-model:value="formData.selectedConfigs"
              :multiple="true"
              :clearable="true"
              placeholder="Pick one or more config(s)"
              :filterable="true"
              service-uri="/readers/configs"
              options-label="name"
            />
          </NFormItemGi>
          <NFormItemGi
            path="selectedReaders"
            label="Reader"
            span="12"
            v-else-if="isSelectedByReader"
          >
            <ArsSmartSelect
              v-model:value="formData.selectedReaders"
              :multiple="true"
              :clearable="true"
              placeholder="Select a reader"
              :filterable="true"
              service-uri="sync/readers"
              options-label="name"
            />
          </NFormItemGi>
          <NFormItemGi
            path="selectedReadzones"
            label="Redzone"
            span="12"
            v-else-if="isSelectedByReadzone"
          >
            <ArsSmartSelect
              v-model:value="formData.selectedReadzones"
              :multiple="true"
              :clearable="true"
              placeholder="Select a readzone"
              :filterable="true"
              service-uri="sync/readzones"
              options-label="name"
            />
          </NFormItemGi>
          <NFormItemGi
            path="username"
            label="Username"
            span="12"
          >
            <NInput
              v-model:value="formData.username"
              placeholder="Enter username"
            />
          </NFormItemGi>
          <NFormItemGi
            path="password"
            label="Pasword"
            span="12"
          >
            <NInput
              v-model:value="formData.password"
              placeholder="Enter password"
              type="password"
            />
          </NFormItemGi>
        </NGrid>
      </NForm>
      <NButton
        id="resetFormSaveBtn"
        type="success"
        class="mx-1 my-1"
        @click="prepareCommands(store.remoteCommandConfigRowData)"
      >
        Submit
      </NButton>
    </NFlex>
  </NModal>
</template>
